import { ApiError, api, hasUnsavedChanges, setIdentityPartition, setIdentityRecheck, setJobsAware, setMoneyAware, setScreensStale, type PartitionIdentity } from './api'
import { businessSettings, setBusinessSettings, setWhatsAppStops, type BusinessSettings } from './business-settings'
import { clearLogoutMark, forgetOfflineIdentity, hasLogoutMark, readOfflineIdentity, saveOfflineIdentity } from './offline-identity'
import { setYearsRound } from './round-config'

// Who is signed in, fetched once at boot and kept in memory (NOT localStorage
// — the role decides which app the phone shows, and a stale copy surviving a
// config change would show the wrong one).
// FROZEN CONTRACT (multi-business, 2026-09-22) — the shape /api/me answers.
// See MULTI-BUSINESS-CONTRACT.md. Owners and workers carry their business and
// its settings (bank_lines is null for a worker); an admin carries no
// business; 'none' is an email with no users row (canSignUp says whether the
// "Set up your business" screen may be offered).
export interface MembershipRound { businessId: number; shortName: string; name: string; colour: string; role: string; current: boolean }
export interface Me {
  rounds?: MembershipRound[]
  // Numbers that asked to stop WhatsApps from Roundly (0081): the owner's whole round, a worker's own customers only.
  waStopped?: string[]
  roundColour?: string
  role: 'owner' | 'worker' | 'admin' | 'none'
  workerName?: string | null
  email?: string
  businessId?: number | null
  // The name "running late" texts introduce; kept for older code, always
  // equal to settings.business_name when settings are present.
  businessName?: string | null
  business?: { id: number; name: string; status: 'trial' | 'active' | 'suspended' | 'closed' } | null
  settings?: BusinessSettings | null
  canSignUp?: boolean
  // Where a new round can be, with each country's zones (Step 2 round 4): the UK always; the US once switched on.
  countries?: Record<string, string[]>
  // The picture for the top corner: their own upload, the business's
  // stand-in (Dropby's logo), or null for the plain initial.
  avatar?: string | null
  hasOwnPhoto?: boolean
  // False only for a worker whose owner has hidden prices from them (0021).
  showPrices?: boolean
  // Calls inside Roundly (0027): an owner of the business calling is set up for.
  calling?: boolean
  // Texts from Roundly switched on for this business (0059), said to the owner only and only when on (Send all, 0093).
  texting?: boolean
  // WhatsApp from Roundly sends payment messages and reminders for this business (Stage 2), owners only, only when on.
  waSends?: boolean
  // What kind of round (0028) and, for an owner on the free months, its last day.
  trade?: string
  // Sign-up's answers (0073; Liam picked B, 15 Oct 2026). Null for rounds set up before.
  worksWith?: 'alone' | 'others' | null
  priceMethod?: 'per_window' | 'house_size' | 'by_area' | null
  defaultCycleDays?: number | null
  trialEndsOn?: string | null
  // The round's sign-up day its free time counts from (0066), with trialEndsOn.
  freeFrom?: string | null
  // The owner of a UK round with free months: More shows Your plan (billing stage 2; never in the iPhone app).
  plan?: boolean
  // A paused or view-only round (billing stage 6): absent while open. The line under the top bar says why.
  access?: { access: 'open' | 'paused' | 'view_only'; why: 'free_time_ended' | 'unpaid' | 'plan_ended' | null; since: string | null; stopped?: boolean }
  // Several jobs per customer (0042) switched on for this round. Absent = off, which every round is until Liam
  // turns one on.
  severalJobs?: boolean
  // Money brought from Squeegee (0043): this round has it, so this app's changes say they record cash amounts.
  broughtMoney?: boolean
  // One-person round (0078; Liam, 19 Oct 2026): the owner chose "Just me" at sign-up or turned Working with others
  // off. Said to the owner only, and only when on. The screens then leave out who is out, worker filters and the rest.
  worksAlone?: boolean
  // The round's country, clock and money (Step 1): read once at start-up (round-config.ts).
  round?: { country: 'GB' | 'US' | 'CA'; timeZone: string; currency: 'GBP' | 'USD' | 'CAD' }
  // The sign-in's business is paused (403 business_suspended). The answer
  // names no one, so role is 'none' and nothing loads or sends; the phone
  // shows "This Roundly account is paused" (bootScreen() === 'paused').
  suspended?: boolean
  // Nobody is signed in (Roundly's own sign-in, 0025): boot shows the
  // sign-in page. Only ever set by the check made at boot.
  signedOut?: boolean
}

let me: Me | null = null
// Has any check finished in this page (the boot check)? Only a LATER answer
// that differs is an identity change to announce.
let answered = false
// Who this page's screens were drawn for: the boot check's answer.
let screensFor = ''
// The screens belong to an account no longer signed in, and the reload was
// held back over unsent work (followIdentity).
let screensStale = false
// Overlapping checks (boot, tab focus, a parked change, another tab's
// broadcast): only the most recently started one may decide.
let latest = 0
// Opened with no signal as the last person signed in on this phone (offline-identity.ts): the time the server
// last confirmed them, or 0 when the server has answered in this page. While set, api.ts is in local-only mode.
let offlineSince = 0

export function openedWithoutSignal(): number {
  return offlineSince
}

export function getMe(): Me | null {
  return me
}

// A one-person round's owner (0078): the simpler screens. Never a worker (a round with a worker is not one person).
export function worksAlone(): boolean {
  return me?.role === 'owner' && me.worksAlone === true
}

export function isWorker(): boolean {
  return me?.role === 'worker'
}

// An owner who does jobs too: they have a name on their visits (People, "I do
// jobs too"), so they get the worker's Today screen as a tab of their own
// (Liam, 2026-09-25). isWorker() still means only "this login is a worker".
export function ownerDoesJobs(): boolean {
  return me?.role === 'owner' && !!me.workerName?.trim()
}

// What boot may draw from the answer:
//   'app'        — a listed user; the shell follows the role
//   'not-a-user' — the sign-in reached the app but is on neither list
//   'paused'     — the sign-in's business is suspended or closed
//   'unknown'    — the check itself failed (offline, session mid-renewal).
// 'unknown' must NEVER fall back to any shell: the owner layout over cached
// owner data would show the wrong person's round on a shared phone.
export type BootScreen = 'app' | 'not-a-user' | 'paused' | 'unknown' | 'signed-out'

export function bootScreen(): BootScreen {
  if (me === null) return 'unknown'
  if (me.signedOut) return 'signed-out'
  if (me.suspended) return 'paused'
  return me.role === 'none' ? 'not-a-user' : 'app'
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function positiveInt(v: unknown): number | null {
  return typeof v === 'number' && Number.isSafeInteger(v) && v > 0 ? v : null
}

// A 200 from /api/me, today's shapes and yesterday's (which had no business:
// an owner or worker without one is business 1, the only business there
// was). undefined = not an answer this app understands.
function readAnswer(body: unknown): Me | undefined {
  if (!isRecord(body)) return undefined
  const email = typeof body.email === 'string' ? body.email : undefined
  const role = body.role
  if (role === 'admin') return { ...body, role, email, businessId: null, settings: null }
  if (role === 'none') return { ...body, role, email, businessId: null, settings: null, canSignUp: body.canSignUp === true }
  if (role !== 'owner' && role !== 'worker') return undefined
  return {
    ...body,
    role,
    email,
    workerName: typeof body.workerName === 'string' ? body.workerName : null,
    businessId: positiveInt(body.businessId) ?? 1,
    settings: isRecord(body.settings) ? (body.settings as unknown as BusinessSettings) : null,
  }
}

// A refusal that still says who is signed in (contract §5). undefined = no
// answer at all (offline, timed out, the Access login page, a server error):
// that decides nothing.
function readRefusal(err: unknown): Me | undefined {
  const status = err instanceof ApiError ? err.status : 0
  const body = err instanceof ApiError ? err.body : null
  const code = err instanceof ApiError ? err.code : undefined
  if (status === 403 && code === 'business_suspended') return { ...body, role: 'none', suspended: true }
  if (status === 401 && code === 'signed_out') return { role: 'none', signedOut: true }
  if (status === 403 && code === 'not_a_user') {
    return {
      role: 'none',
      email: typeof body?.email === 'string' ? body.email : undefined,
      canSignUp: body?.canSignUp === true,
      countries: isRecord(body?.countries) ? (body.countries as Record<string, string[]>) : undefined,
    }
  }
  // Yesterday's server said it in words only. An unlisted sign-in reaches
  // /api/me and is told exactly this — show the "not a Roundly user" screen
  // rather than an owner app that 403s on everything.
  if (/not a Roundly user/.test((err as Error)?.message ?? '')) return { role: 'none' }
  return undefined
}

// Whose storage and requests this answer stands for (see api.ts). A paused
// business, or a sign-in with no email to tell it apart by, gets none.
function partitionOf(m: Me): PartitionIdentity | null {
  if (m.suspended || !m.email) return null
  return { role: m.role, email: m.email, businessId: m.businessId ?? null }
}

// Several jobs on or off is part of it (Astra r1, finding 10): screens drawn while it was off know nothing of a
// customer's jobs, so once Liam switches it they are drawn again (or, holding unsent work, refused) rather than
// sending changes the server would read as made by a screen that knows jobs.
function identityKey(m: Me | null): string {
  if (!m) return ''
  // Working alone too (0078): the Diary, Today and Money are drawn differently, so a change redraws them.
  return [accountKey(m), m.severalJobs ? 'jobs' : '', m.worksAlone ? 'alone' : ''].join('|')
}

// The account alone, without what it can do: a switch changing (several jobs) is the same account, whose unsent work
// still belongs to it and still sends (Astra r2, finding 5).
function accountKey(m: Me | null): string {
  if (!m) return ''
  return [m.role, m.email ?? '', m.businessId ?? '', m.suspended ? 'paused' : ''].join('|')
}
// What the screens on show were drawn with: the account, and whether they know about several jobs.
let screensAccount = ''
let screensJobs = false
let capabilityRecheck = 0

// Is someone other than the account this page drew its screens for signed in
// now (seen on a tab coming back, a change the server parked, or another
// tab's news)? Then every screen on show belongs to the other account, and
// the page reloads and boots afresh for the account signed in now. UNLESS
// unsent work exists only in this page's memory: a photo or voice note
// waiting to send (photo-note / voice-note cancel the event), or a change
// storage refused (api.ts). A reload would destroy it, so the page stays; its
// screens then ask for and change nothing (api.ts), and the work goes once
// its account signs back in. The next answer re-decides, so the reload comes
// as soon as nothing is held any more.
function followIdentity(): void {
  // Same account, only the several-jobs switch changed (Astra r2, finding 5): nothing belongs to anyone else, so no
  // cover and nothing is refused. With nothing waiting in this page's memory the screens are drawn again at once;
  // with a photo or voice note (or a change only this page holds) still to send, they send first, the screens keep
  // the jobs marker they were drawn with, and the redraw follows as soon as nothing is held.
  if (identityKey(me) !== screensFor && accountKey(me) === screensAccount && !screensStale) {
    const held = !window.dispatchEvent(new CustomEvent('dropby:capability-changed', { cancelable: true }))
    if (!held && !hasUnsavedChanges()) {
      if (typeof location !== 'undefined') location.reload()
      return
    }
    setJobsAware(screensJobs)
    if (!capabilityRecheck && typeof window !== 'undefined') capabilityRecheck = window.setTimeout(() => { capabilityRecheck = 0; followIdentity() }, 5_000)
    return
  }
  if (identityKey(me) === screensFor) {
    if (screensStale) {
      screensStale = false
      setScreensStale(false)
      uncoverScreens()
      // Unsent photos and voice notes can go again; their buttons stop
      // saying another account is signed in.
      window.dispatchEvent(new CustomEvent('dropby:identity-restored'))
    }
    return
  }
  const event = new CustomEvent('dropby:identity-changed', { cancelable: true, detail: { formHeld: false } })
  const uploadHeld = !window.dispatchEvent(event)
  if (!uploadHeld && !hasUnsavedChanges()) {
    if (typeof location !== 'undefined') location.reload()
    return
  }
  screensStale = true
  setScreensStale(true)
  coverScreens(uploadHeld, event.detail.formHeld)
}

// ---- the cover --------------------------------------------------------------------
// While the reload is held back, the first account's round must not stay on
// show to whoever is signed in now. One plain panel covers the page and
// everything else is hidden, NOT removed: the unsent work and the screens'
// state stay alive in memory until the first account is back (the cover
// lifts and the page carries on) or the user lets the work go (reload).
const COVER_ID = 'account-switched'
const UPLOAD_WORDS =
  "You're signed in as another account now. Sign back in as the first one to send your unsent photo or voice note, or discard it."
const CHANGES_WORDS =
  "You're signed in as another account now. Sign back in as the first one to save the changes this phone could not store, or discard them."
let cover: { panel: HTMLElement; words: HTMLElement; style: HTMLElement; hidden: HTMLElement[] } | null = null

function coverScreens(uploadHeld: boolean, formHeld = false): void {
  if (typeof document === 'undefined' || !document.body || typeof document.createElement !== 'function') return
  const text = formHeld ? "Your round changed in another tab. Switch back to save this form, or discard it." : uploadHeld ? UPLOAD_WORDS : CHANGES_WORDS
  if (cover) {
    cover.words.textContent = text
    return
  }
  // Everything already on the page is hidden; anything added later (the
  // queue chip redraws, a toast) is hidden by the rule.
  const hidden: HTMLElement[] = []
  for (const child of Array.from(document.body.children) as HTMLElement[]) {
    if (child.hidden) continue
    child.hidden = true
    hidden.push(child)
  }
  const style = document.createElement('style')
  style.textContent = `body > :not(#${COVER_ID}) { display: none !important; }`
  document.head.append(style)
  const panel = document.createElement('div')
  panel.id = COVER_ID
  panel.setAttribute('role', 'alertdialog')
  panel.setAttribute(
    'style',
    'position:fixed;inset:0;z-index:2147483647;display:flex;flex-direction:column;justify-content:center;align-items:flex-start;gap:1rem;padding:1.5rem;background:#fff;color:#1a1a1a;font-size:1.1rem;line-height:1.5;',
  )
  const words = document.createElement('p')
  words.textContent = text
  const discard = document.createElement('button')
  discard.setAttribute('type', 'button')
  discard.textContent = 'Discard and switch'
  // The unsent work lives only in this page's memory: reloading lets it go
  // and boots the app for the account signed in now.
  discard.addEventListener('click', () => location.reload())
  panel.append(words, discard)
  document.body.append(panel)
  cover = { panel, words, style, hidden }
}

function uncoverScreens(): void {
  if (!cover) return
  cover.panel.remove()
  cover.style.remove()
  for (const el of cover.hidden) el.hidden = false
  cover = null
}

export async function loadMe(): Promise<Me | null> {
  const mine = ++latest
  // Logged out on this phone and the server not yet told (no signal at the time): try again now, on EVERY check
  // (boot, tab focus, a parked change); until it goes through, nothing restores and the cookie is never trusted to
  // sign the phone quietly back in (Astra, 1 Oct).
  if (hasLogoutMark()) {
    if (!(await finishLogout())) {
      if (!answered) {
        answered = true
        me = { role: 'none', signedOut: true }
      }
      return me
    }
  }
  let answer: Me | undefined
  let noAnswer = false
  try {
    const raw = await api<unknown>('/api/me')
    answer = readAnswer(raw)
    // Numbers that asked to stop WhatsApps (0081): no WhatsApp opens for them anywhere (business-settings.ts
    // setWhatsAppStops). Only from the newest check: an older answer landing late never undoes a stop (Astra).
    if (mine === latest) setWhatsAppStops((raw as { waStopped?: unknown } | null)?.waStopped)
  } catch (err) {
    answer = readRefusal(err)
    noAnswer = answer === undefined && (err instanceof ApiError ? err.status === 0 : true)
  }
  if (mine !== latest) return me // a later check is under way; its answer decides
  // Signed out mid-use is not a new identity: the screens stay, unsent changes
  // are held as "sign in again" (api.ts), and that button reloads into the
  // sign-in page. Only the check made at boot shows the sign-in page itself.
  // Except when the page was opened with no signal: then the first real word from the server is that this phone
  // is signed out, so it goes to sign-in (the restored person's unsent work stays held for them).
  if (answer?.signedOut && answered) {
    // Either way the server has said this phone is signed out: it must never open as that person with no signal.
    forgetOfflineIdentity()
    if (offlineSince) {
      if (typeof location !== 'undefined') location.reload()
      return me
    }
    answer = undefined
  }
  const first = !answered
  answered = true
  // The boot check got no answer at all: open as the last person signed in on this phone, if the server confirmed
  // them within 3 days, in local-only mode (docs/plans/offline/PLAN-v2.md §2).
  if (first && answer === undefined && noAnswer) {
    const kept = readOfflineIdentity()
    if (kept) {
      me = kept.me
      setYearsRound(me.trade === 'exterior')
      offlineSince = kept.at
      setIdentityPartition(partitionOf(me), { localOnly: true })
      setBusinessSettings(me.settings ?? null)
      // The stops it last heard, so no signal never brings WhatsApp back for a number that asked to stop (Astra).
      setWhatsAppStops(me.waStopped)
      setJobsAware(me.severalJobs === true)
      setMoneyAware(me.broughtMoney === true)
      screensFor = identityKey(me)
      screensAccount = accountKey(me)
      screensJobs = me.severalJobs === true
      watchForSignal()
      return me
    }
  }
  if (answer !== undefined) {
    // The server's word on who this phone is: kept for opening with no signal only if it is an owner or worker of
    // a running round; anything else (signed out, paused, removed, admin) forgets the person kept before, so a
    // phone the server has turned away can never open as them with no signal (Astra, 1 Oct).
    if ((answer.role === 'owner' || answer.role === 'worker') && !answer.suspended) saveOfflineIdentity(answer)
    else forgetOfflineIdentity()
  }
  const wasOffline = offlineSince !== 0
  if (answer !== undefined) offlineSince = 0
  // No answer: whoever was signed in still is (or, at boot, nobody is known
  // and boot shows the retry). Nothing is re-namespaced or dropped.
  if (answer !== undefined) {
    me = answer
    setYearsRound(me.trade === 'exterior')
    // Namespace storage by WHO signed in, and for which business, before
    // anything loads (the business-1 owner keeps the legacy keys; everyone
    // else gets their own; nobody to act for parks everything).
    setIdentityPartition(partitionOf(me))
    // The business's own words for its messages and screens (the builders
    // have no fallback); none for an admin, a sign-in with no business, or a
    // paused one.
    setBusinessSettings(me.settings ?? null)
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('roundly:rounds-updated'))
    // Several jobs on for this round: every change this app makes is marked as made by a screen that knows jobs.
    setJobsAware(me.severalJobs === true)
    // Brought money on this round: every change this app makes says it records cash amounts (api.ts moneyAware).
    setMoneyAware(me.broughtMoney === true)
  }
  if (first) {
    screensFor = identityKey(me)
    screensAccount = accountKey(me)
    screensJobs = me?.severalJobs === true
  }
  else if (answer !== undefined) followIdentity()
  // Signal is back and it is the same person: the screens fetch afresh (and the queue has started sending).
  if (wasOffline && answer !== undefined && identityKey(me) === screensFor && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('dropby:signal-back'))
  }
  return me
}

// Opened with no signal: keep asking, quietly, until the server answers.
let watching = false
function watchForSignal(): void {
  if (watching || typeof window === 'undefined') return
  watching = true
  const tryNow = (): void => {
    if (offlineSince) void recheck()
  }
  window.addEventListener('online', tryNow)
  window.setInterval(tryNow, 20_000)
}

// The server half of a log-out made with no signal.
async function finishLogout(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/logout', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
      credentials: 'same-origin',
      signal: AbortSignal.timeout(10_000),
    })
    if (!res.ok) return false
    clearLogoutMark()
    return true
  } catch {
    return false
  }
}

// Re-checks come in bursts (every parked change asks); one runs at a time,
// and one more follows if any were asked for meanwhile, so the last word is
// always an answer from after the last request.
let running: Promise<Me | null> | null = null
let again = false

function recheck(): Promise<Me | null> {
  if (running) {
    again = true
    return running
  }
  running = (async () => {
    try {
      let result: Me | null
      do {
        again = false
        result = await loadMe()
      } while (again)
      return result
    } finally {
      running = null
    }
  })()
  return running
}

setIdentityRecheck(recheck)

// A tab coming back into view asks again: the sign-in may have changed while
// it was hidden (another account signed in, or the business was paused).
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  document.addEventListener('visibilitychange', () => {
    if (answered && document.visibilityState === 'visible') void recheck()
  })
}

// A worker whose owner has hidden prices (0021). The server already sends
// them none; this decides what the screens say instead (no money in the
// tally, and "how much cash did you take?" on a cash payment).
export function pricesHidden(): boolean {
  return me?.role === 'worker' && me.showPrices === false
}

// Both immediate and queued Settings saves arrive here after the settings memory was updated.
// Apply the acknowledged name first, then fetch the current membership data from the server.
if (typeof window !== 'undefined') {
  window.addEventListener('roundly:business-settings-updated', (event) => {
    const row = (event as CustomEvent).detail as Record<string, unknown>
    if (!me || row.id !== me.businessId) return
    me.settings = businessSettings()
    me.businessName = me.settings.business_name
    const { businessId, settings } = me
    if (me.rounds) me.rounds = me.rounds.map((round) => round.businessId === businessId
      ? { ...round, shortName: settings.short_name } : round)
    saveOfflineIdentity(me)
    window.dispatchEvent(new CustomEvent('roundly:rounds-updated'))
    void loadMe()
  })
}
